import { useEffect, useMemo, useState, useSyncExternalStore, type ReactElement } from 'react';
import { styleBoutonSecondaire } from '../../../../../packages/ui/src/composants/styles.js';
import { COULEURS, styleBase, styleParagraphe } from '../../../../../packages/ui/src/tokens/tokens.js';
import { creerClient, ErreurApi } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { encodeurCanvas, lirePhoto as lirePhotoParDefaut, stockageNavigateur, transportTus } from './adaptateurs.js';
import { creerControleurDocuments } from './controleur-documents.js';
import { creerControleurPhoto, type ControleurPhoto, type EtatPhoto } from './controleur-photo.js';
import { EcranDocuments } from './EcranDocuments.js';
import { ListeDocumentsReelle } from './ListeDocuments.js';
import { EcranPhotoPiece } from './EcranPhotoPiece.js';
import { clientAvecCle } from './envoi-piece.js';
import { ETATS_ECRANS } from './libelles.js';
import type { Encodeur, PhotoBrute } from './photo.js';
import type { StockageLocal, TransportTransfert } from './transfert.js';
import type { DocumentLakou } from './types.js';

/**
 * Écrans branchés dans le registre de la coquille (`ComposantEcran`, `{ ecran }`) : ils créent le client de l'API, chargent les
 * lectures du contrat et branchent les actions. Les dépendances du navigateur sont injectables pour les tests, jamais devinées.
 */
const rechercheCourante = (): string => (typeof window === 'undefined' ? '' : window.location.search);
const fetchGlobal: typeof fetch = (...args) => globalThis.fetch(...args);
const parametre = (recherche: string, nom: string): string | null => new URLSearchParams(recherche).get(nom) || null;

/** Tant que le contrat n'a pas de route de contenu (RFC-L58-001 §1), l'aperçu s'appuie sur la vignette de la version courante. */
const urlContenu = (d: DocumentLakou): string => d.currentVersion.preview.thumbnailUrl ?? '';

export interface DependancesD09 {
  fetch?: typeof fetch;
  /** Partie « ?… » de l'adresse ; le dossier vient de `?dossier=<id>`. */
  recherche?: string;
}

/** D09 · Documents du dossier (équipe MAAT). */
export function EcranD09({ ecran, dependances = {} }: { ecran: Ecran; dependances?: DependancesD09 }): ReactElement {
  const textes = ETATS_ECRANS.d09;
  const controleur = useMemo(
    () =>
      creerControleurDocuments({
        client: creerClient({ fetch: dependances.fetch ?? fetchGlobal }),
        dossierId: parametre(dependances.recherche ?? rechercheCourante(), 'dossier'),
        decisionEnregistree: textes.decisionEnregistree,
        demandeEnregistree: textes.demandeEnregistree,
      }),
    // Le contrôleur vit le temps de l'écran : les dépendances sont lues une fois, au montage.
    [],
  );
  useEffect(() => {
    void controleur.charger();
  }, [controleur]);
  const etat = useSyncExternalStore(controleur.abonner, controleur.etat, controleur.etat);
  const titre = <h1 style={{ ...styleBase('equipe'), fontSize: 28, margin: 0 }}>{ecran.titre}</h1>;
  const paragraphe = styleParagraphe('equipe');

  return (
    <section data-ecran={ecran.id} data-composant="documents">
      {etat.statut === 'sans_dossier' ? <ListeDocumentsReelle {...(dependances.fetch ? { fetchApi: dependances.fetch } : {})} /> : null}
      {etat.statut === 'chargement' ? (
        <>
          {titre}
          <p role="status" style={paragraphe}>
            {textes.chargement}
          </p>
        </>
      ) : null}
      {etat.statut === 'erreur' ? (
        <>
          {titre}
          <p role="alert" style={{ ...paragraphe, color: COULEURS.erreur }}>
            {etat.message}
          </p>
          <button type="button" onClick={() => void controleur.charger()} style={styleBoutonSecondaire('equipe')}>
            {textes.reessayer}
          </button>
        </>
      ) : null}
      {etat.statut === 'pret' ? (
        <>
          <EcranDocuments
            titreEcran={ecran.titre}
            documents={etat.documents}
            filtre={etat.filtre}
            libellesPersonnes={{}}
            libellesBiens={{}}
            urlContenu={urlContenu}
            selection={etat.selection}
            visionneuse={etat.visionneuse}
            versions={etat.versions}
            onFiltre={controleur.filtrer}
            onSelectionner={(id) => void controleur.selectionner(id)}
            onZoom={controleur.zoom}
            onPivoter={controleur.pivoter}
            onPage={controleur.page}
            onExaminer={(decision, id, message) => void controleur.examiner(decision, id, message)}
          />
          {etat.lecture === 'chargement' ? (
            <p role="status" style={paragraphe}>
              {textes.ouverture}
            </p>
          ) : null}
          {etat.lecture === 'erreur' ? (
            <p role="alert" style={{ ...paragraphe, color: COULEURS.erreur }}>
              {etat.messageLecture}
            </p>
          ) : null}
          {etat.examen ? (
            <p
              role={etat.examen.etat === 'erreur' ? 'alert' : 'status'}
              style={etat.examen.etat === 'erreur' ? { ...paragraphe, color: COULEURS.erreur } : paragraphe}
            >
              {etat.examen.message}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export interface DependancesM05 {
  fetch?: typeof fetch;
  /** Partie « ?… » de l'adresse ; la pièce attendue vient de `?piece=<id>`. */
  recherche?: string;
  stockage?: StockageLocal;
  evenements?: EventTarget;
  fabriquerTransport?: (uploadUrl: string) => TransportTransfert;
  encodeur?: Encodeur;
  lirePhoto?: (fichier: File) => Promise<PhotoBrute & { nom: string }>;
  genererCle?: () => string;
  fabriquerUrlApercu?: (octets: Uint8Array, mime: string) => string;
  revoquerUrlApercu?: (url: string) => void;
}

interface Accueil {
  nextAction: { kind: string; label: string };
}

type EtatAccueil =
  { statut: 'sans_piece' | 'chargement' } | { statut: 'pret' | 'hors_ligne'; libelle: string } | { statut: 'erreur'; message: string };

const cleAleatoire = (): string => globalThis.crypto.randomUUID();

/** M05 · Envoyer une pièce par photo (héritier). Passe uniquement par les routes du portail. */
export function EcranM05({ ecran, dependances = {} }: { ecran: Ecran; dependances?: DependancesM05 }): ReactElement {
  const textes = ETATS_ECRANS.m05;
  const [config] = useState(() => {
    const fetchFn = dependances.fetch ?? fetchGlobal;
    return {
      fetchFn,
      client: creerClient({ fetch: fetchFn }),
      requirementId: parametre(dependances.recherche ?? rechercheCourante(), 'piece'),
    };
  });
  const [accueil, setAccueil] = useState<EtatAccueil>({ statut: config.requirementId === null ? 'sans_piece' : 'chargement' });
  const [tentative, setTentative] = useState(0);
  const [controleur, setControleur] = useState<ControleurPhoto | null>(null);
  const [photo, setPhoto] = useState<EtatPhoto>({ phase: 'attente' });

  useEffect(() => {
    if (config.requirementId === null) return;
    let actif = true;
    setAccueil({ statut: 'chargement' });
    config.client.appeler<Accueil>('portail.accueil').then(
      (lu) => {
        if (actif) setAccueil({ statut: 'pret', libelle: lu.nextAction.kind === 'piece' ? lu.nextAction.label : textes.pieceGenerique });
      },
      (erreur: unknown) => {
        if (!actif) return;
        if (erreur instanceof ErreurApi && erreur.code === 'RESEAU') setAccueil({ statut: 'hors_ligne', libelle: textes.pieceGenerique });
        else setAccueil({ statut: 'erreur', message: erreur instanceof ErreurApi ? erreur.messageUtilisateur : textes.chargement });
      },
    );
    return () => {
      actif = false;
    };
  }, [config, tentative, textes]);

  useEffect(() => {
    if (config.requirementId === null) return;
    const ctl = creerControleurPhoto({
      client: config.client,
      clientPourCle: (cle) => clientAvecCle({ fetch: config.fetchFn }, cle),
      requirementId: config.requirementId,
      stockage: dependances.stockage ?? stockageNavigateur(),
      fabriquerTransport: dependances.fabriquerTransport ?? ((url) => transportTus(url, config.fetchFn)),
      encodeur: dependances.encodeur ?? encodeurCanvas,
      genererCle: dependances.genererCle ?? cleAleatoire,
      ...(dependances.evenements === undefined ? {} : { evenements: dependances.evenements }),
      ...(dependances.fabriquerUrlApercu === undefined ? {} : { fabriquerUrlApercu: dependances.fabriquerUrlApercu }),
      ...(dependances.revoquerUrlApercu === undefined ? {} : { revoquerUrlApercu: dependances.revoquerUrlApercu }),
    });
    const arreter = ctl.abonner(() => setPhoto(ctl.etat()));
    setControleur(ctl);
    setPhoto(ctl.etat());
    return () => {
      arreter();
      ctl.liberer();
    };
    // Le contrôleur vit le temps de l'écran : les dépendances sont lues une fois, au montage.
  }, [config]);

  const lire = dependances.lirePhoto ?? ((f: File) => lirePhotoParDefaut(f));
  const titre = <h1 style={{ ...styleBase('client'), fontSize: 24, margin: 0 }}>{ecran.titre}</h1>;
  const paragraphe = styleParagraphe('client');

  return (
    <section data-ecran={ecran.id} data-composant="photo-piece">
      {accueil.statut === 'sans_piece' || accueil.statut === 'chargement' ? (
        <>
          {titre}
          <p role="status" style={paragraphe}>
            {accueil.statut === 'sans_piece' ? textes.sansPiece : textes.chargement}
          </p>
        </>
      ) : null}
      {accueil.statut === 'erreur' ? (
        <>
          {titre}
          <p role="alert" style={{ ...paragraphe, color: COULEURS.erreur }}>
            {accueil.message}
          </p>
          <button type="button" onClick={() => setTentative((n) => n + 1)} style={styleBoutonSecondaire('client')}>
            {textes.reessayer}
          </button>
        </>
      ) : null}
      {accueil.statut === 'pret' || accueil.statut === 'hors_ligne' ? (
        <>
          {accueil.statut === 'hors_ligne' ? (
            <p role="note" style={paragraphe}>
              {textes.horsLigne}
            </p>
          ) : null}
          <EcranPhotoPiece
            titreEcran={ecran.titre}
            etat={photo}
            libellePiece={accueil.libelle}
            onChoisir={(fichier) => {
              // `choisir` ne rejette jamais : une lecture ou un décodage impossible devient l'erreur « photo illisible ».
              if (controleur !== null) void controleur.choisir(Promise.resolve().then(() => lire(fichier)));
            }}
            onEnvoyer={() => void controleur?.envoyer()}
            onReprendre={() => void controleur?.reprendre()}
          />
        </>
      ) : null}
    </section>
  );
}
